// Shared wording for admin actions that cost members money or fail with an API
// reason. Kept in one place so every publish / save / swap dialog says the same
// thing, and unit tested (tests/unit/admin-action-copy.test.ts).

/** "gbp" + 1000 → "£10.00" (minor units → localised currency string). */
export function formatMinor(amountMinor, currency) {
    const code = (currency || 'gbp').toUpperCase();
    try {
        const value = (amountMinor ?? 0) / 100;
        const whole = Number.isInteger(value);
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: code,
            minimumFractionDigits: whole ? 0 : 2,
            maximumFractionDigits: whole ? 0 : 2,
        }).format(value);
    } catch {
        return `${((amountMinor ?? 0) / 100).toFixed(2)} ${code}`;
    }
}

/**
 * The API's `entryFeeImpact` ({payerCount, amountMinor, currency} | null) as one
 * sentence for a publish / save confirm. Null (older API, no fee, already
 * published) or nobody affected → null, so callers show today's copy.
 *
 * Publishing doesn't charge a card: each payer gets an entry fee they owe plus
 * a "fee due" notification. The wording says exactly that.
 *
 * @param impact  the response field
 * @param unit    'player' | 'team' — what one payer is
 */
export function entryFeeImpactText(impact, unit = 'player') {
    const count = impact?.payerCount ?? 0;
    if (!impact || count <= 0 || !(impact.amountMinor > 0)) return null;
    const amount = formatMinor(impact.amountMinor, impact.currency);
    const noun = count === 1 ? unit : `${unit}s`;
    const verb = count === 1 ? 'will owe' : 'will each owe';
    return `${count} ${noun} ${verb} the ${amount} entry fee and get a "fee due" notification.`;
}

/**
 * The API's reason for a failed request, or `fallback`. httpRequest throws the
 * parsed error body ({global: [...], field: [...]}), not an Error, so
 * `e.message` is usually undefined.
 */
export function apiErrorMessage(e, fallback = 'Something went wrong. Please try again') {
    const global = e?.global;
    if (Array.isArray(global) && global.length && global[0]) return global[0];
    if (e && typeof e === 'object') {
        for (const key of Object.keys(e)) {
            const v = e[key];
            if (key !== 'global' && Array.isArray(v) && v.length && typeof v[0] === 'string') return v[0];
        }
    }
    return fallback;
}

/** `setAlertMessage` payload for an error toast (one argument, red). */
export function errorAlert(e, fallback) {
    return {message: apiErrorMessage(e, fallback), color: 'error'};
}

/** "1 match" / "3 matches", "1 player" / "8 players". */
export function countNoun(count, singular, plural = `${singular}s`) {
    const n = count ?? 0;
    return `${n} ${n === 1 ? singular : plural}`;
}
